import React, { Fragment, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import { connect } from "react-redux";

// Helpers
import _ from "lodash";
import formatFoldersForSelect from "../utils/formatFoldersForSelect";

// State
import {
  getCampaign,
  createCampaignFolder,
  selectCampaignFolder,
  getNestedCampaignFolders,
} from "../redux/actions/campaignActions";

// UI Components
import Loader from "./Loader";
import { FolderPlus, Folder, Mail } from "react-feather";
import CreateCampaignFolderModal from "./Modal.CreateCampaignFolder";
import { Collapse } from "reactstrap";

// Globals
const MAX_FOLDER_NESTING = 1;

const FolderList = ({ data, user, children, nestLevel, toggleModal, onSelectedFolder, onSelectCampaign }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Filter out empty folders for read only users
  if (user.group_id_bint === 1) {
    children = children.filter((child) => {
      if (child.data.campaign_id_bint) return true;
      return child.children.length;
    });
  }

  // If Folder has children, render as dropdown
  if (children.length || nestLevel < MAX_FOLDER_NESTING) {
    const toggle = () => {
      onSelectedFolder(data.folder_id_bint)
      //setIsOpen(!isOpen);
    }
    return (
      <li className={"sidebar-link"} style={{ padding: "0" }}>
        <span
          data-toggle="collapse"
          className={"sidebar-link " + (!isOpen ? "collapsed" : "")}
          onClick={toggle}
          aria-expanded={isOpen ? "true" : "false"}
        >
          <Folder size={18} className="align-middle mr-2" />

          <span className="align-middle">{data.title_vch}</span>
        </span>

        <Collapse isOpen={isOpen}>
          <ul id="item" className={"sidebar-dropdown list-unstyled"}>
            {/* ADD MODAL BUTTON */}
            {user.group_id_bint > 1 && nestLevel < MAX_FOLDER_NESTING && (
              <li className="sidebar-link" onClick={() => toggleModal(data.folder_id_bint)}>
                <FolderPlus size={18} className="align-middle mr-2" />
                Add Sub-Folder
              </li>
            )}

            {children.map((props) => (
              <FolderList
                key={props.data.folder_id_bint}
                user={user}
                nestLevel={++nestLevel}
                toggleModal={toggleModal}
                onSelectedFolder={() => onSelectedFolder(props.data)}
                onSelectCampaign={onSelectCampaign}
                {...props}
              />
            ))}
          </ul>
        </Collapse>
      </li>
    );
  }

  // Otherwise, render as either a campaign or single folder with no content
  if (user.group_id_bint > 1) {
    return data.campaign_id_bint ? (
      <li className={"sidebar-item"}>
        <span className={"sidebar-link"} onClick={() => onSelectCampaign(data.campaign_id_bint)}>
          <Mail size={18} className="align-middle mr-2" style={{ opacity: 1 }} />
          {data.title_vch}
        </span>
      </li>
    ) : (
      <li className={"sidebar-item"}>
        <span className={"sidebar-link"}>
          <Folder size={18} className="align-middle mr-2" />
          {data.title_vch}
        </span>
      </li>
    );
  }

  // If read only user - show campaign / hide empty folders
  return data.campaign_id_bint ? (
    <li className={"sidebar-item"}>
      <span className={"sidebar-link"} onClick={() => onSelectCampaign(data.campaign_id_bint)}>
        <Mail size={18} className="align-middle mr-2" style={{ opacity: 1 }} />
        {data.title_vch}
      </span>
    </li>
  ) : null;
};

// UI Components
const SidebarCampaignFolders = ({
  history,
  isLoading,
  user,
  folders,
  nestedCampaignFolders,
  createCampaignFolder,
  getCampaign,
  getNestedCampaignFolders,
  selectCampaignFolder
}) => {
  // Only execute on load - empty dependency array (eslint line disables warning that fails build)
  useEffect(() => {
    getNestedCampaignFolders();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State
  const [showModal, setModal] = useState(false);
  const [parentFolder, setParentFolder] = useState();

  // Show loading graphic to let user know awaiting content
  if (isLoading) {
    return <Loader height="50px" />;
  }

  // Convert flat array to a nested tree of parent and children folders
  const formattedOptions = formatFoldersForSelect(folders);

  // UI
  const toggleModal = (value = null) => {
    const assignToFolder = _.find(formattedOptions, { value });
    setParentFolder(assignToFolder);
    setModal(!showModal);
  };

  const selectCampaign = (campaign_id) => {
    getCampaign(campaign_id, true);
    history.push(`/campaign/view/${campaign_id}`);
  };

  const selectedFolder = (data) => {

    selectCampaignFolder(data);
    history.push(`/folder/view/${data.folder_id_bint}`);
  }
  return (
    <Fragment>
      {/* ADD MODAL BUTTON - don't allow read only users to add folder */}
      {user.group_id_bint > 1 && (
        <li className="sidebar-link" onClick={() => toggleModal()}>
          <FolderPlus size={18} className="align-middle mr-2" />
          Add Folder
        </li>
      )}

      {/* FOLDER TREE */}
      {nestedCampaignFolders.map(({ data, children }) =>
        user.group_id_bint > 1 || children.length ? (
          <FolderList
            key={data.folder_id_bint}
            user={user}
            data={data}
            children={children}
            nestLevel={0}
            toggleModal={toggleModal}
            onSelectedFolder={() => selectedFolder(data)}
            onSelectCampaign={selectCampaign}
          />
        ) : null
      )}

      {/* ADD FOLDER MODAL */}
      <CreateCampaignFolderModal
        showModal={showModal}
        folders={folders}
        parentFolder={parentFolder}
        toggleModal={toggleModal}
        createCampaignFolder={createCampaignFolder}
        onParentFolderChanged={setParentFolder}
      />
    </Fragment>
  );
};

SidebarCampaignFolders.propTypes = {
  isLoading: PropTypes.bool.isRequired,
  user: PropTypes.object.isRequired,
  folders: PropTypes.array.isRequired,
  nestedCampaignFolders: PropTypes.array.isRequired,
  getCampaign: PropTypes.func.isRequired,
  getNestedCampaignFolders: PropTypes.func.isRequired,
  selectCampaignFolder: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
  isLoading: state.campaigns.loading.nested_campaign_folders || false,
  user: state.user.user,
  folders: state.campaigns.folders,
  nestedCampaignFolders: state.campaigns.nestedCampaignFolders,
});

export default connect(mapStateToProps, {
  getCampaign,
  createCampaignFolder,
  getNestedCampaignFolders,
  selectCampaignFolder
})(withRouter(SidebarCampaignFolders));
